import { useEffect, useMemo, useRef, useState } from 'react'
import { ResponsiveContainer, LineChart, CartesianGrid, XAxis, YAxis, Line, Tooltip, Legend, Bar, PieChart, Pie, Cell, ComposedChart } from 'recharts'
import { getChartPalette } from '../../chartPalette'

const CHART_COLORS = getChartPalette()
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

const COLOR_RATE = CHART_COLORS[9]
const COLOR_TOTAL_RATE = '#666666'
const MAX_BAR_SIZE = 90

const getMonthNumber = (month) => {
  if (month == null) return null
  if (typeof month === 'number') return month
  const n = Number(month)
  if (!Number.isNaN(n) && n >= 1 && n <= 12) return n
  const idx = MONTHS.findIndex((m) => m.toLowerCase() === String(month).toLowerCase())
  return idx >= 0 ? idx + 1 : null
}

const CustomLegend = ({ payload }) => {
  if (!payload || payload.length === 0) return null
  return (
    <div className="custom-legend-container">
      {payload.map((entry) => (
        <div className="legend-item" key={entry.value}>
          <span className="legend-color-dot" style={{ background: entry.color }} />
          <span className="legend-label">{entry.value}</span>
        </div>
      ))}
    </div>
  )
}

const transformSoldConnectedData = (rows = []) => {
  if (!Array.isArray(rows) || rows.length === 0) return { month: [], quarter: [], year: [] }

  const monthlyData = {}
  const quarterlyData = {}
  const yearlyData = {}

  rows.forEach((row) => {
    const year = Number(row.year)

    const month = getMonthNumber(row.month)
    const monthKey = `${year}-${month}`

    const quarter = Math.floor((month - 1) / 3) + 1
    const quarterKey = `${year}-Q${quarter}`

    if (!year || !month) return

    if (!monthlyData[monthKey]) monthlyData[monthKey] = { year, month, soldCount: 0, soldConnectedCount: 0, connectedCount: 0 }
    monthlyData[monthKey].soldCount += Number(row.soldCount || 0)
    monthlyData[monthKey].soldConnectedCount += Number(row.soldConnectedCount || 0)
    monthlyData[monthKey].connectedCount += Number(row.connectedCount || 0)

    if (!quarterlyData[quarterKey]) quarterlyData[quarterKey] = { year, quarter, soldCount: 0, soldConnectedCount: 0, connectedCount: 0 }
    quarterlyData[quarterKey].soldCount += Number(row.soldCount || 0)
    quarterlyData[quarterKey].soldConnectedCount += Number(row.soldConnectedCount || 0)
    quarterlyData[quarterKey].connectedCount += Number(row.connectedCount || 0)

    if (!yearlyData[year]) yearlyData[year] = { year, soldCount: 0, soldConnectedCount: 0, connectedCount: 0 }
    yearlyData[year].soldCount += Number(row.soldCount || 0)
    yearlyData[year].soldConnectedCount += Number(row.soldConnectedCount || 0)
    yearlyData[year].connectedCount += Number(row.connectedCount || 0)
  })

  const withRate = (item) => {
    const connectivityRate = item.soldCount > 0 ? parseFloat(((item.soldConnectedCount / item.soldCount) * 100).toFixed(1)) : 0
    const totalConnectivityRate = item.soldCount > 0 ? parseFloat(((item.connectedCount / item.soldCount) * 100).toFixed(1)) : 0
    return {
      ...item,
      connectivityRate,
      totalConnectivityRate,
    }
  }

  const monthArr = Object.values(monthlyData)
    .sort((a, b) => a.year - b.year || a.month - b.month)
    .map((d) =>
      withRate({
        date: `${MONTHS[d.month - 1]} ${d.year}`,
        soldCount: d.soldCount,
        soldConnectedCount: d.soldConnectedCount,
        connectedCount: d.connectedCount,
      }),
    )

  const quarterArr = Object.values(quarterlyData)
    .sort((a, b) => a.year - b.year || a.quarter - b.quarter)
    .map((d) =>
      withRate({ date: `Q${d.quarter}/${d.year}`, soldCount: d.soldCount, soldConnectedCount: d.soldConnectedCount, connectedCount: d.connectedCount }),
    )

  const yearArr = Object.values(yearlyData)
    .sort((a, b) => a.year - b.year)
    .map((d) => withRate({ date: String(d.year), soldCount: d.soldCount, soldConnectedCount: d.soldConnectedCount, connectedCount: d.connectedCount }))

  return { month: monthArr, quarter: quarterArr, year: yearArr }
}

const RateDotWithLabel = (props) => {
  const { cx, cy, payload } = props
  if (cx == null || cy == null) return null
  const value = payload?.connectivityRate
  return (
    <g>
      <circle cx={cx} cy={cy} r={4} fill={COLOR_RATE} stroke={COLOR_RATE} />
      {value != null && (
        <text x={cx} y={cy - 10} textAnchor="middle" fontSize={11} fontWeight={600} fill={COLOR_RATE}>
          {`${value}%`}
        </text>
      )}
    </g>
  )
}

const TotalRateDotWithLabel = (props) => {
  const { cx, cy, payload } = props
  if (cx == null || cy == null) return null
  const value = payload?.totalConnectivityRate
  return (
    <g>
      <circle cx={cx} cy={cy} r={4} fill={COLOR_TOTAL_RATE} stroke={COLOR_TOTAL_RATE} />
      {value != null && (
        <text x={cx} y={cy - 10} textAnchor="middle" fontSize={11} fontWeight={600} fill={COLOR_TOTAL_RATE}>
          {`${value}%`}
        </text>
      )}
    </g>
  )
}

const SoldConnectionChart = ({ data = [], chartType = 'stackedBar', chartFrequency = 'month' }) => {
  const containerRef = useRef(null)

  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })
  const transformed = useMemo(() => transformSoldConnectedData(data), [data])
  const chartData = transformed[chartFrequency] || []

  const maxCount = useMemo(
    () => chartData.reduce((m, d) => Math.max(m, Number(d.soldCount || 0), Number(d.soldConnectedCount || 0), Number(d.connectedCount || 0)), 0),
    [chartData],
  )

  const chartDataWithScaledRate = useMemo(() => {
    if (!maxCount) return chartData
    return chartData.map((d) => ({
      ...d,
      connectivityRateScaled: (Number(d.connectivityRate || 0) / 100) * maxCount,
      totalConnectivityRateScaled: (Number(d.totalConnectivityRate || 0) / 100) * maxCount,
    }))
  }, [chartData, maxCount])

  const colorMap = {
    soldCount: CHART_COLORS[7],
    soldConnectedCount: CHART_COLORS[10],
    connectivityRate: COLOR_RATE,
    connectedCount: CHART_COLORS[6],
    totalConnectivityRate: COLOR_TOTAL_RATE,
  }

  const tooltipFormatter = (value, name, { payload }) => {
    if (name === 'Connectivity Rate') return [`${payload.connectivityRate}%`, name]
    if (name === 'Total Connectivity Rate') return [`${payload.totalConnectivityRate}%`, name]
    if (name === 'Monthly Total Connections') return [payload.connectedCount, name]
    return [value, name]
  }

  const renderChart = () => {
    switch (chartType) {
      case 'bar':
        return (
          <ComposedChart data={chartDataWithScaledRate} margin={{ top: 30, right: 30, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis allowDecimals={false} />
            <Tooltip formatter={tooltipFormatter} />
            <Legend content={CustomLegend} />
            <Bar dataKey="soldCount" maxBarSize={MAX_BAR_SIZE} name="Monthly Sold Units" fill={colorMap.soldCount} />
            <Bar dataKey="soldConnectedCount" maxBarSize={MAX_BAR_SIZE} name="Monthly Sold Connections" fill={colorMap.soldConnectedCount} />
            <Bar dataKey="connectedCount" maxBarSize={MAX_BAR_SIZE} name="Monthly Total Connections" fill={colorMap.connectedCount} />
            <Line
              dataKey="connectivityRateScaled"
              name="Connectivity Rate"
              stroke={colorMap.connectivityRate}
              strokeWidth={2}
              type="monotone"
              dot={RateDotWithLabel}
              isAnimationActive={false}
              connectNulls
            />
            <Line
              dataKey="totalConnectivityRateScaled"
              name="Total Connectivity Rate"
              stroke={colorMap.totalConnectivityRate}
              strokeWidth={2}
              type="monotone"
              dot={TotalRateDotWithLabel}
              isAnimationActive={false}
              connectNulls
            />
          </ComposedChart>
        )
      case 'stackedBar':
        return (
          <ComposedChart data={chartDataWithScaledRate} margin={{ top: 30, right: 30, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis allowDecimals={false} />
            <Tooltip formatter={tooltipFormatter} />
            <Legend content={CustomLegend} />
            <Bar dataKey="soldCount" name="Monthly Sold Units" maxBarSize={MAX_BAR_SIZE} fill={colorMap.soldCount} stackId="stack" />
            <Bar dataKey="soldConnectedCount" name="Monthly Sold Connections" maxBarSize={MAX_BAR_SIZE} fill={colorMap.soldConnectedCount} stackId="stack" />
            <Bar dataKey="connectedCount" name="Monthly Total Connections" maxBarSize={MAX_BAR_SIZE} fill={colorMap.connectedCount} stackId="stack" />
            <Line
              dataKey="connectivityRateScaled"
              name="Connectivity Rate"
              stroke={colorMap.connectivityRate}
              strokeWidth={2}
              type="monotone"
              dot={RateDotWithLabel}
              isAnimationActive={false}
              connectNulls
            />
            <Line
              dataKey="totalConnectivityRateScaled"
              name="Total Connectivity Rate"
              stroke={colorMap.totalConnectivityRate}
              strokeWidth={2}
              type="monotone"
              dot={TotalRateDotWithLabel}
              isAnimationActive={false}
              connectNulls
            />
          </ComposedChart>
        )
      case 'pie': {
        const totals = chartData.reduce(
          (acc, d) => {
            acc.sold += Number(d.soldCount || 0)
            acc.soldConnected += Number(d.soldConnectedCount || 0)
            acc.connected += Number(d.connectedCount || 0)
            return acc
          },
          { sold: 0, soldConnected: 0, connected: 0 },
        )
        const overallRate = totals.sold > 0 ? parseFloat(((totals.soldConnected / totals.sold) * 100).toFixed(1)) : 0
        const overallTotalRate = totals.sold > 0 ? parseFloat(((totals.connected / totals.sold) * 100).toFixed(1)) : 0
        const pieData = [
          { name: 'Monthly Sold Units', value: totals.sold, fill: CHART_COLORS[7] },
          { name: 'Monthly Sold Connections', value: totals.soldConnected, fill: CHART_COLORS[10] },
          { name: 'Monthly Total Connections', value: totals.connected, fill: CHART_COLORS[4] },
        ]
        return (
          <PieChart>
            <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="53%" outerRadius={250} label isAnimationActive={false}>
              {pieData.map((entry, idx) => (
                <Cell key={entry.name} fill={entry.fill} />
              ))}
            </Pie>
            <Legend
              content={CustomLegend}
              payload={[
                { value: 'Monthly Sold Units', color: CHART_COLORS[7], type: 'square' },
                { value: 'Monthly Sold Connections', color: CHART_COLORS[10], type: 'square' },
                { value: 'Monthly Total Connections', color: CHART_COLORS[4], type: 'square' },
                { value: `Connectivity Rate: ${overallRate}%`, color: COLOR_RATE, type: 'line' },
                { value: `Total Connectivity Rate: ${overallTotalRate}%`, color: COLOR_TOTAL_RATE, type: 'line' },
              ]}
            />
            <Tooltip />
            {/* <text x="50%" y="48%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: 18, fontWeight: 600, fill: COLOR_RATE }}>
              {overallRate}%
            </text>
            <text x="50%" y="58%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: 18, fontWeight: 600, fill: COLOR_TOTAL_RATE }}>
              {overallTotalRate}%
            </text> */}
          </PieChart>
        )
      }
      case 'line':
      default:
        return (
          <LineChart data={chartDataWithScaledRate} margin={{ top: 30, right: 30, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis allowDecimals={false} />
            <Tooltip formatter={tooltipFormatter} />
            <Legend content={CustomLegend} />
            <Line dataKey="soldCount" name="Monthly Sold Units" stroke={CHART_COLORS[7]} strokeWidth={2} type="monotone" dot={false} />
            <Line dataKey="soldConnectedCount" name="Monthly Sold Connections" stroke={CHART_COLORS[10]} strokeWidth={2} type="monotone" dot={false} />
            <Line dataKey="connectedCount" name="Monthly Total Connections" stroke={colorMap.connectedCount} strokeWidth={2} type="monotone" dot={false} />
            <Line
              dataKey="connectivityRateScaled"
              name="Connectivity Rate"
              stroke={COLOR_RATE}
              strokeWidth={2}
              type="monotone"
              dot={RateDotWithLabel}
              isAnimationActive={false}
              connectNulls
            />
            <Line
              dataKey="totalConnectivityRateScaled"
              name="Total Connectivity Rate"
              stroke={COLOR_TOTAL_RATE}
              strokeWidth={2}
              type="monotone"
              dot={TotalRateDotWithLabel}
              isAnimationActive={false}
              connectNulls
            />
          </LineChart>
        )
    }
  }

  useEffect(() => {
    const observer = new window.ResizeObserver((entries) => {
      if (entries[0]) {
        const { width, height } = entries[0].contentRect
        setDimensions({ width, height })
      }
    })
    if (containerRef.current) observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [])

  return (
    <div ref={containerRef} style={{ width: '100%', height: 650 }} className="chart-responsive-wrapper">
      <div className="chart-min-width">
        {dimensions.width > 0 && dimensions.height > 0 && (
          <ResponsiveContainer width="100%" height="100%">
            {renderChart()}
          </ResponsiveContainer>
        )}
      </div>
    </div>
  )
}

export default SoldConnectionChart
